import { LitElement, html, css, unsafeCSS } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { keyed } from "lit/directives/keyed.js";
import { icons } from "../../../Icons.js";
import componentStyle from "./PropContainer.css?raw";
import { animate } from "motion";

export class RDPropsetContainer extends LitElement {
  static styles = css`
    ${unsafeCSS(componentStyle)}
  `;
  dropDown = [];

  static properties = {
    // [{group:{style:boxed|pilled|normal, css:string, type:toggle|selection|normal,selectedItem: string, selectedIndex: number, items:[icon, name, tooltip, visible, selected]}}]
    iconGroups: { type: Array },
    devicesGroup: { type: Object },
    themesGroup: { type: Object },
    modified: { type: Boolean, reflect: true },
    name: { type: String, reflect: true },
    label: { type: String, reflect: true },
    onItemClicked: { type: Function },
    onItemHovered: { type: Function },
    isDevices: { type: Boolean, reflect: true },
    isThemes: { type: Boolean, reflect: true },
    visible: { type: Boolean, reflect: true },
    isParentCollapsed: { type: Boolean },
    categoryCollapsed: { type: Boolean, reflect: true },
    groupCollapsed: { type: Boolean, reflect: true },
    atIndex: { type: Number },
    atBefore: { type: String },
    atAfter: { type: String },
    showOverflowControls: { type: Boolean, reflect: true },
    clearIconClicked: { type: Function },
    clearIconVisible: { type: Boolean },

    size: {
      type: String,
      reflect: true,
    }, //full, small
  };

  constructor() {
    super();
    this.modified = false;
    this.name = "";
    this.label = "";
    this.size = "full";
    this.onItemClicked = null;
    this.onItemHovered = null;
    this.modified = false;
    this.isDevices = true;
    this.isThemes = true;
    this.visible = true;
    this.isParentCollapsed = false;
    this.showOverflowControls = false;
    this.categoryCollapsed = true;
    this.groupCollapsed = false;
    this.clearIconVisible = true;
    this._key = _createID();

    if (this.iconGroups == null) {
      this.iconGroups = [];
    }

    this.devicesGroup = null;
    this.themesGroup = null;

    // Subscribe to DeviceManager updates
    this._boundHandleDeviceManagerUpdate = this._handleDeviceManagerUpdate.bind(this);
    if (window["DeviceManager"]) {
      this._deviceManagerUnsubscribe = window["DeviceManager"].on("updated", this._boundHandleDeviceManagerUpdate);
    }

    document.addEventListener(canvasEventsLabels.themeChanged, (e) => {
      if (this.isParentCollapsed) return;
      this.createThemes();
      this.requestUpdate();
    });
  }
  get element() {
    return this;
  }

  /**
   * Handle DeviceManager updates
   * @private
   */
  _handleDeviceManagerUpdate(data) {
    if (this.isParentCollapsed) return;

    // Update devices when device list changes (type-based events)
    if (data.type && data.type.includes("device")) {
      this.createDevices();
      this.requestUpdate();
      return;
    }

    // Update devices when active device changes (state-based events)
    if (data.previous && data.current && data.previous.activeDevice !== data.current.activeDevice) {
      this.createDevices();
      this.requestUpdate();
    }
  }

  createDevices() {
    if (!this.isDevices) {
      this.devicesGroup = null;
      return;
    }

    const deviceManager = window["DeviceManager"];
    if (!deviceManager) {
      console.warn("[PropContainer] DeviceManager not available");
      this.devicesGroup = null;
      return;
    }

    // Get only visible devices from DeviceManager
    const visibleDevices = deviceManager.getVisibleDevices();
    const currentState = deviceManager.getState();
    const activeDeviceName = currentState.activeDevice;

    // Check if active device is in visible list
    const activeDeviceInVisibleList = activeDeviceName && visibleDevices.some((d) => d.name.toLowerCase() === activeDeviceName);

    let devices = visibleDevices.map((d) => {
      return {
        icon: d.icon || "",
        name: d.name || "",
        tooltip: d.tooltip || d.label || d.name || "",
        visible: true, // Already filtered to visible only
        selected: d.name.toLowerCase() === activeDeviceName,
      };
    });

    // Add a "Custom" option when no device is selected OR when selected device is hidden
    if (!activeDeviceName || !activeDeviceInVisibleList) {
      devices.unshift({
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="12" viewBox="0 0 24 24" fill="currentColor">
<text x="12" y="18" text-anchor="middle" font-size="20" font-weight="bold">*</text>
</svg>`,
        name: "Custom",
        tooltip: "Custom",
        visible: true,
        selected: true,
      });
    }

    this.devicesGroup = {
      name: "Devices",
      style: "normal",
      type: "toggle",
      items: devices,
    };
  }

  createThemes() {
    if (!this.isThemes) {
      this.themesGroup = null;
      return;
    }

    let themes = window.Builder.themes.map((t) => {
      return {
        icon: t.icon || "",
        name: t.name || "",
        tooltip: t.tooltip || "",
        visible: t.visible || true,
        selected: t.selected || false,
      };
    });

    this.themesGroup = {
      name: "Themes",
      style: "normal",
      type: "toggle",
      items: themes,
    };
  }
  visibilityChanged(collapsed) {
    this.isParentCollapsed = collapsed;
    if (!collapsed) {
      this.createDevices();
      this.createThemes();
      this.requestUpdate();
    }
  }
  updateSideBar() {
    this.dispatchEvent(
      new CustomEvent("update-sidebar", {
        detail: {},
      }),
    );
  }
  iconList = new Map();
  groupsConfigItems = new Map();
  _hide(isanimate = false) {
    if (isanimate) {
      animate(this, { opacity: 0 }, { duration: 0.5 }).then(() => {
        this.style.display = "none";
      });
    } else {
      this.style.display = "none";
    }
  }
  _show(isanimate = false) {
    this.style.display = "block";
    if (isanimate) {
      animate(this, { opacity: [0, 1] }, { duration: 0.5 });
    }
  }
  firstUpdated() {
    this.createDevices();
    this.createThemes();
    this._UpdateIcons();
    if (this.visible) {
      this._show();
    } else {
      this._hide();
    }
    if (!this) return;
    if (this.size.trim().toLowerCase() == "small") {
      this.style.gridColumn = "1 / 2";
    } else {
      this.style.gridColumn = "1 / span 2";
    }
    requestAnimationFrame(() => {
      this._updateCarousel();
    });
  }

  updated(changedProperties) {
    if (changedProperties.has("isDevices")) {
      this.createDevices();
    }
    if (changedProperties.has("isThemes")) {
      this.createThemes();
    }

    this._UpdateIcons();
    if (changedProperties.has("visible")) {
      if (this.visible) {
        this._show();
      } else {
        this._hide();
      }
    }
    if (changedProperties.has("modified")) {
      this.updateSideBar();
    }
    if (changedProperties.has("size")) {
      if (!this) return;
      if (this.size.trim().toLowerCase() == "small") {
        this.style.gridColumn = "auto / span 1";
      } else {
        this.style.gridColumn = "auto / span 2";
      }
    }
    requestAnimationFrame(() => {
      this._updateCarousel();
    });
  }

  _itemClicked(item) {
    if (this.onItemClicked && typeof this.onItemClicked === "function") {
      //this.onItemClicked(item);
    }
    this.dispatchEvent(
      new CustomEvent("item-clicked", {
        detail: { item },
      }),
    );
    let target = this.shadowRoot.querySelector("#" + item.id);

    let items = this.groupsConfigItems.get(item.groupName);
    this.panel = {
      // target: this,
      size: "auto",
      placement: "bottom",
      offsetY: 5,
      offsetX: -10,
      target: target,
      onSelect: (dropdownItem) => {
        // Find group in either devicesGroup, themesGroup, or iconGroups
        let group = null;
        if (this.devicesGroup?.name === item.groupName) {
          group = this.devicesGroup;
        } else if (this.themesGroup?.name === item.groupName) {
          group = this.themesGroup;
        } else {
          group = this.iconGroups.find((g) => g.name === item.groupName);
        }

        let selectedItems = group?.items?.filter((i) => i.selected);
        selectedItems?.forEach((si) => {
          si.selected = false;
        });

        if (group) {
          if (group.type === "toggle") {
            let clickedItem = group?.items?.find((i) => i.name === dropdownItem.value);
            if (clickedItem) {
              clickedItem.selected = true;
              if (group.name.trim().toLowerCase() === "devices" && this.isDevices) {
                // Use DeviceManager to select device
                if (window["DeviceManager"]) {
                  window["DeviceManager"].selectDevice(clickedItem.name, true, "propset");
                }
                // Keep Builder sync for backward compatibility
                if (window.Builder && window.Builder.deviceChanged) {
                  window.Builder.deviceChanged(clickedItem.name);
                }
              }
              if (group.name.trim().toLowerCase() === "themes" && this.isThemes) {
                window.Builder.themeChanged(clickedItem.name);
                CanvasManager.darkMode = clickedItem.name.toLowerCase() === "dark";
              }
              if (this.onItemClicked && typeof this.onItemClicked === "function") {
                this.onItemClicked(item).bind(this);
              }
            }
          }
        }
        this.requestUpdate();
      },
      onOpen: () => {
        // console.log('Dropdown opened');
      },
      onClose: () => {
        //console.log('Dropdown closed');
      },
      onHover: (item) => {
        // console.log('Hovered pseudo-class:', item.value);
      },
      items: items || [],
    };
    DropdownPanel.show(this.panel);
    this.requestUpdate();
  }

  _renderIconGroup(iconGroup) {
    // Combine devices, themes, and user iconGroups in the correct order
    const allGroups = [];

    // Add devices first if enabled
    if (this.devicesGroup) {
      allGroups.push(this.devicesGroup);
    }

    // Add themes second if enabled
    if (this.themesGroup) {
      allGroups.push(this.themesGroup);
    }

    // Add user-defined groups
    if (iconGroup && iconGroup.length > 0) {
      allGroups.push(...iconGroup);
    }

    if (allGroups.length === 0) {
      return html``;
    }

    this.iconList.clear();
    this.groupsConfigItems.clear();
    return html` <div class="icon-groups">${allGroups.map((group) => this._renderGroup(group))}</div> `;
  }

  _renderGroup(group) {
    if (!group || !group.name || !group.items || group.items.length === 0) {
      return html``;
    }
    let groupName = group.name;
    let style = group.style || "normal";
    let type = group.type || "normal";
    let css = group.css || "";

    return html` <div class="icon-group ${style} ${type}" group="${groupName}" style="${css}">${this._renderGroupItems(group.items, groupName)}</div> `;
  }

  _renderGroupItems(items, groupName) {
    if (!items || items.length === 0) {
      return html``;
    }
    let confItems = [];
    items.forEach((item) => {
      item.groupName = groupName;
      confItems.push({
        leftIcon: item.icon,
        value: item.name || "",
        selected: item.selected,
      });
    });
    this.groupsConfigItems.set(groupName, confItems);
    return html`
      <div class="icon-items">
        ${items.map((item) => {
          item.groupName = groupName;
          return this._renderGroupItem(item);
        })}
      </div>
    `;
  }

  _renderGroupItem(item) {
    if (!item || !item.name || item.visible === false || !item.groupName) {
      return html``;
    }
    let icon = item.icon || "";
    let id = item.id || _createID();
    if (!item.id) {
      item.id = id;
    }
    if (icon && id) {
      this.iconList.set(id, icon);
    }
    return html` <div class="icon-item ${item.selected ? "selected" : ""}" title="${item.tooltip || ""}" group="${item.groupName}" @click="${(e) => this._itemClicked(item)}" id="${id}">
      <slot name="${id}"></slot>
    </div>`;
  }

  _UpdateIcons() {
    if (this.iconList.size === 0) {
      return;
    }
    //console.log('Updating icons...', this.iconList);
    try {
      let existingIcons = this.shadowRoot.host.querySelectorAll(".icon-content");
      existingIcons.forEach((iconEl) => {
        iconEl.remove();
      });
    } catch (e) {
      console.error("Error querying existing icons:", e);
      return;
    }
    for (let [id, icon] of this.iconList) {
      let iconElement = document.createElement("span");
      iconElement.style.display = "contents";
      iconElement.style.width = "1rem";
      iconElement.style.height = "1rem";
      iconElement.className = "icon-content";
      iconElement.innerHTML = `<style>svg{width: 1rem;}</style> ${icon}`;
      iconElement.slot = id;
      try {
        this.shadowRoot.host.appendChild(iconElement);
      } catch (e) {
        console.error("Error appending icon element:", e);
      }
    }
  }

  _highlight() {
    if (this._highlightTimeout) {
      clearTimeout(this._highlightTimeout);
    }
    const container = this.shadowRoot.querySelector(".prop-container");
    if (container) {
      container.classList.add("highlighted");
    }
    this._highlightTimeout = setTimeout(() => {
      container.classList.remove("highlighted");
    }, 5000);
  }
  _removeHighlight() {
    const container = this.shadowRoot.querySelector(".prop-container");
    if (container) {
      container.classList.remove("highlighted");
      this._highlightTimeout = null;
    }
  }
  connectedCallback() {
    super.connectedCallback();
    // Use window resize instead of ResizeObserver for better stability
    this._setupWindowResize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
    // Unsubscribe from DeviceManager
    if (this._deviceManagerUnsubscribe) {
      this._deviceManagerUnsubscribe();
    }
    window.removeEventListener("resize", this._handleWindowResize);
    this.removeEventListener("click", this._handleTopbarClick);
    document.removeEventListener("mousedown", this._handleOutsideClick);
  }
  _setupWindowResize() {
    this._updateCarousel();
    // Bind the handler so we can remove it later
    this._handleWindowResize = () => {
      // Clear any pending timeout to throttle resize events
      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
      }

      // Throttle resize events - check after user stops resizing for 200ms
      this._resizeTimeout = setTimeout(() => {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          this._updateCarousel();
        });
      }, 200); // 200ms throttle - responsive but not too aggressive
    };

    // Listen to window resize with passive option for better scroll performance
    window.addEventListener("resize", this._handleWindowResize, { passive: true });
  }
  _updateCarousel() {
    if (!this.showOverflowControls) return;
    const container = this.shadowRoot.querySelector(".prop-body");
    if (!container) return;
    let isScroll = this.visuallyOverflows(container);
    let leftArrow = this.shadowRoot.querySelector(".lftCroller");
    let rightArrow = this.shadowRoot.querySelector(".rgtCroller");
    // console.log('Checking overflow for prop container:', this.label);
    // Check if the container is overflowing
    if (isScroll) {
      leftArrow.style.display = "flex";
      rightArrow.style.display = "flex";
      console.log("prop is overflowing");
    } else {
      leftArrow.style.display = "none";
      rightArrow.style.display = "none";
      console.log("prop is not overflowing");
    }
  }
  visuallyOverflows(el, eps = 0.5) {
    const c = el.getBoundingClientRect();
    for (const child of el.children) {
      const r = child.getBoundingClientRect();
      if (r.left < c.left - eps || r.right > c.right + eps) return true;
    }
    return false;
  }
  _scrollLeft() {
    const container = this.shadowRoot.querySelector(".prop-body-inner");
    container.scrollBy({ left: -100, behavior: "smooth" });
  }

  _scrollRight() {
    const container = this.shadowRoot.querySelector(".prop-body-inner");
    container.scrollBy({ left: 100, behavior: "smooth" });
  }
  forceUpdate() {
    this._key = _createID();
    this.requestUpdate();
  }

  renderBody() {
    return html`<div class="prop-body-content">some content here</div>`;
  }
  _clearIconClicked() {
    if (this.clearIconClicked && typeof this.clearIconClicked === "function") {
      this.clearIconClicked.bind(this)();
    }
  }
  render() {
    return html`${keyed(
      this._key,
      html`
        <div class="prop-container  ${this.modified ? "modified" : ""}" @click="${() => this._removeHighlight()}">
          <div class="prop-header">
            <div class="prop-label" style="${!this.label ? "display:none;" : ""}">${this.label}</div>
            <div class="groups">${this._renderIconGroup(this.iconGroups)}</div>
          </div>

          <div class="prop-body">
            <span class="clearIcon" title="Reset to default" style="display: ${this.modified && this.clearIconVisible ? "inline-flex" : "none"};" @click=${() => this._clearIconClicked()}> ${unsafeHTML(icons.xTimes)} </span>
            <div class="lftCroller" style="display:none" @click=${this._scrollLeft}>${unsafeHTML(icons.chevronLeft)}</div>
            <div class="rgtCroller" style="display:none" @click=${this._scrollRight}>${unsafeHTML(icons.chevronRight)}</div>
            <div class="prop-body-inner">${this.renderBody()}</div>
          </div>
        </div>
      `,
    )}`;
  }
}

if (!customElements.get("rd-propset-container")) {
  customElements.define("rd-propset-container", RDPropsetContainer);
}

function _createID() {
  return "id-" + Math.random().toString(36).substr(2, 16);
}
